// 레이드 4인 넷 판 연결(docs/RAID-PRD.md 0장 2단계): RaidClient(네트워크) ↔ GameSim(고정 60Hz) 연결점. game.raidNet이 이것이다. partyLink.ts를 본떴다.
// - 'go' → 레이드 판(r2 / raid_wyvern)을 열고 RaidController를 넷 모드로 둔다(g.raid.netMode). 서버 첫 상태(sync)가 오면 영웅을 섬의 내 시작 자리에 세워
//   기존 방 진입(flow.enterRoom → g.raid.enterBoss — 문 닫힘·비룡 등장)을 그대로 쓴다. 'go'를 같은 판에서 다시 받으면(재접속) 판을 다시 열지 않는다.
// - 판 동안 보스·용병·경고·불 장판은 서버 꼭두각시다: 보스 위치·방향·체력·상태·비행·페이즈·광폭·경직·표적·도발은 서버 스냅 보간값,
//   용병 칸은 RaidState.mercs[merc](기존 렌더·HUD가 그대로 그린다, 쓰이지 않는 칸은 visible=false), 다른 사람은 view.peers(PeersVisual).
//   로컬 보스 두뇌·용병 두뇌·불 장판 피해 계산은 돌지 않는다. 내 영웅 피격만 로컬이다.
// - 경고: 'tel' → 같은 모양·위치의 지면 경고(진행 틱 = 서버 지금 틱 − born). 서버 판정 틱(due)에 내 영웅이 모양 안이면 기존 hurtHero 한 번(피해 = raidNet strike와 같은 식).
//   'boom' n이면 그 경고를 터뜨린다. 'fire' → 불 장판(내 영웅만 0.5초마다 로컬 burn).
// - 내 타격은 combat.damageBoss가 hit(d)를 보내고(로컬 HP는 예측만, 1 아래로 내리지 않는다), 처치는 서버 boss dying/dead 사건으로만 정한다.
// - 내 영웅이 쓰러지면(heroDown) down을 보내고 쓰러진 채 서버가 일으켜 주기(mev Revive)를 기다린다 — 레이드 실패가 아니다. 서버가 사람 칸을 쓰러뜨려도(mev Down) 똑같다.
// - end → 결과(flow 결과 장면 재사용: 성공 Result·실패 Defeat) → 몇 초 뒤 마을. 보상은 우편함으로 간다. 연결이 끝내 끊기면 마을.
// 고정 스텝·프레임 경로에서 새 객체를 만들지 않는다(사건 콜백·순회 콜백은 생성자에서 한 번 만든다, 경고·장판·용병 표는 고정 배열).
import { Ev, HitFlag, TeleSource } from '../shared/events';
import { makePeers, type FieldPeer } from '../shared/fieldNet';
import { REVIVE } from '../shared/content/consumables';
import { HEAL, MERC_COUNT, MERC_REVIVE, RAID_XP_MULT, TAUNT, WYVERN, WyvernPattern, raidFirstId, type RaidTuning } from '../shared/content/raid';
import { tuneOf } from '../shared/raidNet';
import { XP_BOSS, XpCause } from '../shared/growth';
import { RAID_ARENA, RAID_REVIVE_TICKS, RBoss, RaidAct, MevKind, type RaidEvent, type RaidSync } from '../shared/raidNet';
import type { RaidNetResultView, RaidNetView } from '../shared/raidNetView';
import { BossKind, BossState, HeroAct, Scene } from '../shared/types';
import { telegraphHit } from './combat';
import { StarBit } from './flow';
import type { GameSim } from './game';
import { MercEvent } from './raid';
import type { RaidClient, RaidNetEvent, RaidTrack } from './raidNet';
import { TIMER_MAX } from './state';

const TICK_HZ = 60;
/** 결과를 보여 준 뒤 마을로 가기까지(읽을 시간) */
export const RAID_END_LEAVE_TICKS = 12 * TICK_HZ;
/** 다시 붙기를 이만큼 이어도 안 되면 판을 버리고 마을로(서버 자리 유지 90초 + 여유) */
const RETRY_GIVE_UP_TICKS = 95 * TICK_HZ;
/** 서버가 일으켜 주지 않을 때(메시지 유실) 로컬로 일어나는 여유(남은 틱 0이 된 뒤) */
const REVIVE_GRACE_TICKS = 5 * TICK_HZ;
/** 내 타격 직후 서버 스냅이 오기 전까지 로컬 보스 체력(예측)을 보이는 틱 */
const PRED_TICKS = 30;
/** 접속 중인데 서버 방이 로비로 돌아갔고 end가 안 온다: 이만큼 기다린 뒤 판이 끝난 것으로 본다 */
export const RAID_LOBBY_WAIT_TICKS = 8 * TICK_HZ;
const TEL_MAX = 16;
const PEER_SLOTS = 4;
/** 서버 섬 검사(inArena 반경 r − 0.3)보다 안쪽으로 눌러 보낸다 — 가장자리에서 서버가 되돌리지(correct) 않게 */
const SEND_LIMIT = RAID_ARENA.r - 0.5;
const EVADE_TICKS = 30;
export const RAID_LABEL = '잿불 비룡';

class RaidNetViewState implements RaidNetView {
  active = false;
  attached = false;
  waiting = false;
  barrier = false;
  reviveTicks = 0;
  hitsSent = 0;
  hurtTaken = 0;
  downs = 0;
  teles = 0;
  you = -1;
  readonly peers: FieldPeer[] = makePeers(PEER_SLOTS);
  result: RaidNetResultView | null = null;
  readonly resultBox: RaidNetResultView = { seq: 0, ok: false, reason: '', ms: 0, eligible: false, n: 0, gold: 0, set: '', legend: '', items: 0, weekLeft: -1, leaveTicks: 0 };
}

export class RaidNetLink {
  client: RaidClient | null = null;
  readonly view = new RaidNetViewState();
  /** 서버 칸 → 용병 번호(MERCS, -1 = 사람·빈칸) */
  readonly mercOfSlot = new Int8Array(PEER_SLOTS).fill(-1);
  /** 경고 표: 서버 경고 번호 n(-1 = 빈 칸), 로컬 경고 슬롯, 서버 born·due 틱, 피해 배율, 무거운 타격 */
  private readonly telN = new Int32Array(TEL_MAX).fill(-1);
  private readonly telSlot = new Int16Array(TEL_MAX).fill(-1);
  private readonly telBorn = new Int32Array(TEL_MAX);
  private readonly telDue = new Int32Array(TEL_MAX);
  private readonly telMult = new Float32Array(TEL_MAX);
  private readonly telHeavy = new Uint8Array(TEL_MAX);
  /** 경고의 주인 칸(잿불 낙인, -1 = 일반) */
  private readonly telOwner = new Int8Array(TEL_MAX).fill(-1);
  private readonly mercAct = new Int8Array(MERC_COUNT).fill(-1);
  private readonly evadeLeft = new Uint16Array(MERC_COUNT);
  private mercSeen = 0;
  private serial = -1;
  private entered = false;
  private ended = false;
  private runId = '';
  /** 이 판의 수치(start가 난이도로 고른다 — tuneOf 캐시 객체, 프레임마다 만들지 않음) */
  /** 같은 틱 낙인 영웅 피해 합(flushBrand) */
  private brandDmg = 0;
  private brandHeavy = false;
  private brandX = 0;
  private brandZ = 0;
  private tn: RaidTuning = tuneOf({ raid: 'raid_wyvern', diff: 'normal' });
  private retryTicks = 0;
  private lobbyTicks = 0;
  /** 판 시작 장벽 신호: 'go' 뒤 장면이 자리 잡은 첫 몇 틱(렌더가 한 번 돌 시간) 지나 한 번 보낸다. 재접속 go마다 다시 */
  private loadedSent = true;
  private loadTicks = 0;
  /** 내 칸 Revive 사건의 서버 틱·내가 쓰러진 서버 틱 — 오래된 스냅이 쓰러짐/일어남을 뒤집지 못하게 */
  private reviveK = -1;
  private downK = -1e9;
  private evK = 0;
  private profileTicks = 0;
  /** 내 타격 예측: 보낸 피해와 보낸 틱(링 버퍼) — PRED_TICKS 지난 것은 버린다(서버가 반영했거나 버렸다) */
  private readonly hitDmg = new Float64Array(128);
  private readonly hitAt = new Int32Array(128).fill(-1e9);
  private hitHead = 0;
  private tickNo = 0;
  private lastState = -1;
  private lastServerHp = 0;
  private reviveGrace = 0;
  /** 마지막으로 본 상태 바뀜 기준 틱(경고 born·사건 틱) — stateTick을 서버와 맞추는 데 쓴다 */
  private changeRef = -1;
  private readonly onEvent = (e: RaidNetEvent): void => this.handle(e);
  private readonly eachSlot = (k: RaidTrack): void => this.syncSlot(k);

  constructor(private readonly g: GameSim) {}

  /** 넷 판 진행 중(go ~ 마을로) */
  get active(): boolean { return this.view.active; }

  /** main.ts가 만든 연결층을 붙인다(하나만). null이면 뗀다(진행 중 판은 마을로) */
  attach(client: RaidClient | null): void {
    if (this.client === client) return;
    if (this.view.active) this.leave();
    this.client = client;
    this.view.attached = client !== null;
  }

  /** 내 타격(combat.damageBoss): 서버로 보내고 로컬 체력 예측을 잠깐 유지한다 */
  hit(damage: number): void {
    const c = this.client;
    if (!this.view.active || !c) return;
    if (damage > 0) {
      c.hit(damage); this.view.hitsSent = c.hitsSent;
      this.hitDmg[this.hitHead] = damage; this.hitAt[this.hitHead] = this.tickNo; this.hitHead = (this.hitHead + 1) & 127;
    }
  }

  /** 내 영웅이 쓰러졌다(flow.defeat 대신): 패배 화면 없이 쓰러진 채 서버가 일으켜 주기를 기다린다 */
  heroDown(send = true): void {
    const g = this.g, h = g.hero;
    h.hp = 0; h.act = HeroAct.Dead; h.actTick = 0; h.invulnerable = false; h.y = 0;
    g.awakening.cancel();
    g.skillFx.clear();
    g.arrows.clear();
    this.view.downs++;
    this.downK = this.client ? Math.round(this.client.serverTick()) : -1e9;
    this.view.reviveTicks = RAID_REVIVE_TICKS; this.reviveGrace = REVIVE_GRACE_TICKS;
    if (send) this.client?.down();
    g.flow.objectiveLabel = '쓰러졌다! 동료가 버티는 동안 잠시 기다리면 일어난다.';
  }

  /** 서버가 일으켰다: HP 50%로 그 자리에서 */
  private reviveHero(): void {
    const g = this.g, h = g.hero;
    if (h.act !== HeroAct.Dead) return;
    h.hp = Math.max(1, Math.round(h.maxHp * MERC_REVIVE.hpRatio));
    g.heroController.revive(REVIVE.invulnTicks);
    const e = g.emit(Ev.HeroHeal); e.x = h.x; e.z = h.z; e.a = h.hp; e.b = 0;
    this.view.reviveTicks = 0; this.reviveGrace = 0;
    g.flow.objectiveLabel = `${WYVERN.name}을(를) 쓰러뜨리세요. 경고 장판을 피하고 흩어져 불 장판을 줄이세요.`;
  }

  /** 서버가 사람 칸을 쓰러뜨렸다(대략 체력 0): 이미 쓰러져 있으면 무시 */
  private forceDown(): void {
    const h = this.g.hero;
    if (h.act === HeroAct.Dead || h.hp <= 0) return;
    this.heroDown(false);
  }

  /** 고정 스텝마다(GameSim.step): 사건 처리 → 서버 상태 적용 → 부활·결과 타이머 → 내 위치 송신 */
  step(): void {
    const c = this.client;
    if (!c) return;
    c.drain(this.onEvent);
    this.flushBrand();
    const v = this.view;
    // 로비 대기 중 영웅·레벨 변경 동기화(1초마다 비교만 — 바뀌었을 때만 보낸다)
    if (++this.profileTicks >= 60) { this.profileTicks = 0; c.syncProfile(); }
    if (!v.active) return;
    this.tickNo++;
    const g = this.g, h = g.hero;
    // 화면이 바뀌었다(마을로·다시 도전 단추 등 다른 경로로 판을 떠났다): 연결을 놓는다
    if (g.flow.sceneSerial !== this.serial) { this.detach(); return; }
    if (this.ended) {
      const r = v.resultBox;
      if (r.leaveTicks > 0 && --r.leaveTicks === 0) this.leave();
      return;
    }
    // 판이 사라졌다: 연결 종료·다시 붙기 포기
    if (c.status === 'closed') {
      // 유예가 지난 재접속은 서버가 판에서 뺐다(run-expired): 안내를 보이고 마을로
      if (c.error === 'run-expired') this.finish({ t: 'end', ok: false, reason: 'expired', runId: this.runId, ms: g.flow.elapsedTicks * 1000 / TICK_HZ, dmg: [], eligible: false, reward: null, weekLeft: -1 });
      else this.leave();
      return;
    }
    if (c.status === 'retrying') { if (++this.retryTicks > RETRY_GIVE_UP_TICKS) { c.close(); this.leave(); return; } }
    else this.retryTicks = 0;
    // 끊긴 사이 판이 끝났다(정산 뒤 재접속하면 서버는 로비만 알린다 — end를 다시 안 보낸다): 잠시 기다려도 end가 없으면 마을로
    const rm = c.room;
    if (c.status === 'online' && rm && (rm.phase === 'lobby' || (rm.runId !== '' && rm.runId !== this.runId))) {
      if (++this.lobbyTicks > RAID_LOBBY_WAIT_TICKS) { this.lobbyTicks = 0; this.finish({ t: 'end', ok: false, reason: 'lost', runId: this.runId, ms: g.flow.elapsedTicks * 1000 / TICK_HZ, dmg: [], eligible: false, reward: null, weekLeft: -1 }); return; }
    } else this.lobbyTicks = 0;
    if (!this.loadedSent && ++this.loadTicks >= 3) { this.loadedSent = true; c.loaded(); }
    // 서버 카운트다운이 끝났는데 아직 판이 시작 전(count)이면 동료를 기다리는 중
    v.barrier = !!c.room && c.room.phase === 'count' && Date.now() >= c.startAt;
    // 서버 첫 상태를 받으면 섬에 오른다 → flow.step이 방 진입(문 닫힘·비룡 등장)을 연다
    if (!this.entered) {
      if (!c.synced || g.flow.scene !== Scene.Dungeon) return;
      const me = Math.max(0, Math.min(RAID_ARENA.spawns.length - 1, c.you));
      const sp = RAID_ARENA.spawns[me];
      g.snapHero(sp.x, sp.z);
      h.facing = Math.PI; // 비룡은 섬 북쪽(z 작은 쪽)에 내려앉는다
      this.entered = true; v.waiting = false;
      return;
    }
    if (g.flow.roomState[0] === 0) return; // 방 진입은 이 틱 flow.step이 한다
    c.sample();
    const est = c.serverTick();
    this.syncBoss(c);
    this.mercSeen = 0;
    for (let i = 0; i < PEER_SLOTS; i++) { const p = v.peers[i]; p.active = false; p.visible = false; }
    c.slots.forEach(this.eachSlot);
    const mercs = g.raid.state.mercs;
    for (let i = 0; i < MERC_COUNT; i++) { const m = mercs[i]; m.visible = (this.mercSeen & (1 << i)) !== 0; if (this.evadeLeft[i] > 0) this.evadeLeft[i]--; }
    this.stepTeles(est);
    this.flushBrand();
    this.stepFires();
    const s = g.raid.state;
    if (s.tauntTicks > 0) s.tauntTicks--;
    // 쓰러진 채 기다리기: 표시용 남은 틱 + 서버 일으키기가 오지 않을 때의 안전 장치
    if (v.reviveTicks > 0) v.reviveTicks--;
    else if (h.act === HeroAct.Dead && this.reviveGrace > 0 && --this.reviveGrace === 0) this.reviveHero();
    // 내 위치(섬 안쪽으로 눌러서) — 쓰러져 있는 동안에도 보낸다(서버는 쓰러진 칸의 위치를 쓰지 않는다)
    let sx = h.x, sz = h.z;
    const dx = sx - RAID_ARENA.cx, dz = sz - RAID_ARENA.cz, d = Math.hypot(dx, dz);
    if (d > SEND_LIMIT) { sx = RAID_ARENA.cx + dx / d * SEND_LIMIT; sz = RAID_ARENA.cz + dz / d * SEND_LIMIT; }
    const act = h.act === HeroAct.Attack || h.act === HeroAct.Skill || h.act === HeroAct.Awaken ? 1 : 0;
    c.update(sx, sz, h.facing, act);
  }

  // ── 사건 ──────────────────────────────────────────
  private handle(e: RaidNetEvent): void {
    switch (e.t) {
      case 'go': this.start(e.mercs, e.runId, e.again, e.diff); return;
      case 'end': this.finish(e); return;
      case 'sync': if (this.view.active) this.onSync(e.s); return;
      case 'correct': if (this.view.active) this.g.snapHero(e.x, e.z); return;
      case 'ev': if (this.view.active && !this.ended) this.onRaidEvent(e.e, e.k); return;
    }
  }

  /** 'go': 레이드 판을 연다(같은 판의 재접속 go면 그대로 둔다) */
  private start(mercs: readonly number[], runId: string, again: boolean, diff: 'normal' | 'hard' = 'normal'): void {
    const g = this.g, c = this.client;
    if (!c) return;
    for (let i = 0; i < PEER_SLOTS; i++) this.mercOfSlot[i] = mercs[i] === undefined ? -1 : mercs[i];
    if (again && this.view.active && this.runId === runId) { this.loadedSent = false; this.loadTicks = 0; return; }
    // 파티 판 중에는 레이드를 열지 않는다(두 넷 연결이 한 판을 두고 싸우지 않게) — 서버 자리를 비운다
    if (g.party.active) { c.leave(); c.error = 'party-active'; return; }
    // 하드 방(C13)이면 하드 판(보스 HP·피해·낙인 수치가 서버 raidNet tuneOf와 같다)
    g.flowController.loadMap('r2', diff === 'hard' ? 'raid_wyvern.hard' : 'raid_wyvern');
    // 판 수치는 판 시작에 한 번 고른다(60Hz step 안에서 인자 객체를 만들지 않는다 — #337 Codex P1)
    this.tn = tuneOf({ raid: 'raid_wyvern', diff: diff === 'hard' ? 'hard' : 'normal' });
    const f = g.flow;
    if (f.scene !== Scene.Dungeon) { c.leave(); return; } // 보상 카드를 고르는 중 등 — 장면이 거절했다: 서버 자리를 비운다
    const v = this.view, s = g.raid.state;
    v.active = true; v.waiting = true; v.reviveTicks = 0; v.hitsSent = 0; v.hurtTaken = 0; v.downs = 0; v.teles = 0; v.result = null; v.you = c.you;
    this.brandDmg = 0; this.brandHeavy = false;
    this.runId = runId; this.ended = false; this.retryTicks = 0; this.entered = false; this.hitAt.fill(-1e9);
    this.loadedSent = false; this.loadTicks = 0;
    this.lobbyTicks = 0; this.reviveK = -1; this.downK = -1e9; this.lastState = -1; this.lastServerHp = 0; this.reviveGrace = 0; this.changeRef = -1; this.mercSeen = 0;
    this.serial = f.sceneSerial;
    this.clearTeles(false);
    this.mercAct.fill(-1); this.evadeLeft.fill(0);
    // 넷 모드: 로컬 보스 두뇌·용병·불 장판 계산을 끄고 용병 칸은 서버가 채울 때까지 숨긴다
    g.raid.netMode = true;
    for (let i = 0; i < MERC_COUNT; i++) s.mercs[i].visible = false;
    const wl = c.room ? c.room.weekLeft : -1;
    if (wl >= 0) { s.weekClears = Math.max(0, s.weekLimit - wl); s.practice = wl === 0; }
    f.objectiveLabel = '서버와 호흡을 맞추는 중… 곧 둥지로 들어간다.';
  }

  /** 서버 전체 상태(시작·재접속): 경고·불 장판을 서버 값으로 다시 짓고 내 쓰러짐을 맞춘다 */
  private onSync(s: RaidSync): void {
    const g = this.g, c = this.client;
    if (!c) return;
    this.clearTeles(false);
    for (const f of g.raid.state.fires) f.active = false;
    const est = c.serverTick();
    for (let i = 0; i < s.teles.length; i++) { const t = s.teles[i]; this.addTele(t.n, t.pattern, t.shape, t.x, t.z, t.r, t.a, t.w, t.born, t.due, t.mult, t.heavy, est, t.o ?? -1); }
    for (let i = 0; i < s.fires.length; i++) { const f = s.fires[i]; this.spawnFire(f.x, f.z, f.r, f.ttl, f.clock, false); }
    const me = s.slots.find((p) => p[0] === c.you);
    if (me && this.entered) {
      if (me[8] > 0) { if (g.hero.act !== HeroAct.Dead) this.forceDown(); }
      else if (g.hero.act === HeroAct.Dead) this.reviveHero();
    }
  }

  private onRaidEvent(e: RaidEvent, k: number): void {
    const g = this.g, c = this.client, b = g.boss, s = g.raid.state;
    if (!c) return;
    this.evK = k;
    switch (e.e) {
      case 'pat': {
        // 잿불 낙인 시작 알림은 낙인 경고('tel', 주인 칸)마다 따로 낸다(c = 그 주인의 로컬 칸 비트)
        if (e.id === WyvernPattern.brand) { s.pattern = e.id; this.changeRef = k; break; }
        const ev = g.emit(Ev.BossPattern); ev.x = b.x; ev.z = b.z; ev.a = e.id; ev.b = BossKind.Wyvern; ev.c = 0;
        // 공격 패턴 번호만 지금 패턴으로 둔다(페이즈 2·광폭 알림은 진행 중 패턴을 덮지 않는다)
        if (e.id !== WyvernPattern.phase2 && e.id !== WyvernPattern.enrage) s.pattern = e.id;
        this.changeRef = k;
        break;
      }
      case 'tel': {
        const t = e.t;
        this.addTele(t.n, t.pattern, t.shape, t.x, t.z, t.r, t.a, t.w, t.born, t.due, t.mult, t.heavy, c.serverTick(), t.o ?? -1);
        this.changeRef = t.born;
        break;
      }
      case 'boom': {
        const i = this.teleIndex(e.n);
        if (i >= 0) this.resolveTele(i);
        break;
      }
      case 'fire': this.spawnFire(e.x, e.z, e.r, e.ttl, WYVERN.fire.every, true); break;
      case 'hurt': this.onHurt(e.s, e.d, e.hp); break;
      case 'mev': this.onMev(e.s, e.k, e.a, e.c); break;
      case 'boss': {
        if (e.k === 'stagger') {
          this.clearTeles(false);
          const ev = g.emit(Ev.GuardBreak); ev.x = b.x; ev.z = b.z; ev.a = 1; ev.b = 0; ev.c = WYVERN.staggerTicks;
        } else if (e.k === 'dying') {
          this.clearTeles(false);
          for (const f of s.fires) f.active = false;
          const ev = g.emit(Ev.BossDefeated); ev.x = b.x; ev.z = b.z; ev.a = -1; ev.b = BossKind.Wyvern;
        }
        this.changeRef = k;
        break;
      }
    }
  }

  private onHurt(slot: number, d: number, hp: number): void {
    const g = this.g, mi = this.mercOfSlot[slot];
    void hp;
    if (mi === undefined || mi < 0) return; // 사람 칸: 내 피격은 로컬 판정, 다른 사람 숫자는 그리지 않는다
    const m = g.raid.state.mercs[mi];
    m.hurtTick = 0;
    const ev = g.emit(Ev.Hit); ev.x = m.x; ev.y = 1; ev.z = m.z; ev.a = d; ev.b = 3; ev.c = mi; ev.d = Math.atan2(m.x - g.boss.x, m.z - g.boss.z); ev.flags = HitFlag.Ally;
    this.mercEvent(mi, MercEvent.Hurt, d, 0);
  }

  private onMev(slot: number, kind: number, a: number, cv: number): void {
    const g = this.g, c = this.client;
    if (!c) return;
    const mi = this.mercOfSlot[slot], me = slot === c.you, s = g.raid.state;
    switch (kind) {
      case MevKind.Attack:
        if (mi >= 0) {
          const m = s.mercs[mi];
          m.actTick = 0;
          this.mercEvent(mi, MercEvent.Attack, a, 0);
          if (a > 0) {
            const b = g.boss, ev = g.emit(Ev.Hit);
            ev.x = b.x; ev.y = 1.5; ev.z = b.z; ev.a = a; ev.b = 1; ev.c = mi + 1; ev.d = Math.atan2(b.x - m.x, b.z - m.z); ev.flags = HitFlag.Ally;
          }
        }
        break;
      case MevKind.Heal: {
        // a = 받는 칸, c = 양(서버 대략 체력 기준). 내 영웅이면 최대 HP 12%를 실제로 회복한다
        const best = a === c.you ? 0 : (this.mercOfSlot[a] ?? -1) >= 0 ? this.mercOfSlot[a] + 1 : -1;
        if (a === c.you) {
          const h = g.hero;
          if (h.hp > 0 && h.act !== HeroAct.Dead) {
            const amount = Math.min(h.maxHp - h.hp, Math.round(h.maxHp * HEAL.ratio));
            if (amount > 0) { h.hp += amount; const e = g.emit(Ev.HeroHeal); e.x = h.x; e.z = h.z; e.a = amount; e.b = 2; }
          }
        }
        // 다른 사람이 받는 치유는 연출 대상이 없어(파티 연출은 영웅·용병 칸만 안다) 효과음만 낸다
        if (mi >= 0 && best >= 0) this.mercEvent(mi, MercEvent.Heal, best, cv);
        break;
      }
      case MevKind.Taunt:
        if (mi >= 0) { s.tauntTicks = a > 0 ? a : TAUNT.ticks; this.mercEvent(mi, MercEvent.Taunt, a > 0 ? a : TAUNT.ticks, 0); }
        break;
      case MevKind.Down:
        if (me) this.forceDown();
        else if (mi >= 0) { s.mercs[mi].act = RaidAct.Down; this.mercEvent(mi, MercEvent.Down, a, 0); }
        break;
      case MevKind.Revive:
        if (me) { this.reviveK = this.evK; this.reviveHero(); }
        else if (mi >= 0) this.mercEvent(mi, MercEvent.Revive, a, 0);
        break;
      case MevKind.Dodge:
        if (mi >= 0) { this.evadeLeft[mi] = EVADE_TICKS; this.mercEvent(mi, MercEvent.Dodge, 0, 0); }
        break;
    }
  }

  private mercEvent(i: number, kind: number, a: number, cv: number): void {
    const m = this.g.raid.state.mercs[i], e = this.g.emit(Ev.RaidMerc);
    e.x = m.x; e.z = m.z; e.a = i; e.b = kind; e.c = a; e.d = cv;
  }

  // ── 경고 ──────────────────────────────────────────
  private teleIndex(n: number): number {
    for (let i = 0; i < TEL_MAX; i++) if (this.telN[i] === n) return i;
    return -1;
  }

  private addTele(n: number, pattern: number, shape: number, x: number, z: number, r: number, a: number, w: number, born: number, due: number, mult: number, heavy: boolean, est: number, owner = -1): void {
    if (this.teleIndex(n) >= 0) return;
    let i = -1;
    for (let k = 0; k < TEL_MAX; k++) if (this.telN[k] < 0) { i = k; break; }
    if (i < 0) return;
    const g = this.g, slot = g.allocateTelegraph(TeleSource.Boss, BossKind.Wyvern);
    if (slot < 0) return;
    const tg = g.telegraphs[slot];
    tg.shape = shape as typeof tg.shape; tg.x = x; tg.z = z; tg.radius = r; tg.angle = a; tg.width = w;
    tg.total = Math.max(1, due - born);
    // 늦게 도착했으면 늦은 만큼 앞당겨 채운다(서버 지금 틱 − born)
    tg.tick = Math.max(0, Math.min(tg.total, est - born));
    this.telN[i] = n; this.telSlot[i] = slot; this.telBorn[i] = born; this.telDue[i] = due; this.telMult[i] = mult; this.telHeavy[i] = heavy ? 1 : 0; this.telOwner[i] = owner;
    if (owner >= 0) {
      this.syncBrand();
      const c = this.client, local = c && owner === c.you ? 0 : this.mercOfSlot[owner] >= 0 ? this.mercOfSlot[owner] + 1 : -1;
      const ev = g.emit(Ev.BossPattern); ev.x = x; ev.z = z; ev.a = WyvernPattern.brand; ev.b = BossKind.Wyvern; ev.c = local >= 0 ? 1 << local : 0; ev.d = owner;
    }
    g.raid.state.pattern = pattern;
  }

  /** 경고 i 판정 + 터뜨리기: 내 영웅이 모양 안이면 hurtHero 한 번(서버 strike와 같은 피해식). 한 번 하고 표에서 뺀다 */
  private resolveTele(i: number): void {
    const g = this.g, slot = this.telSlot[i], h = g.hero;
    this.telN[i] = -1; this.telSlot[i] = -1;
    if (slot < 0) return;
    const tg = g.telegraphs[slot];
    const owner = this.telOwner[i], tn = this.tn, enr = g.raid.state.enraged ? WYVERN.enrage.dmgMult : 1;
    this.telOwner[i] = -1;
    if (owner >= 0 && tn.brand) {
      // 잿불 낙인(하드): 내가 주인이면 늘 맞고(bearerDmg), 아니면 원 안일 때 crowdDmg — 서버 strike와 같은 식
      const me = this.client ? this.client.you : -1;
      if (tg.active && h.hp > 0 && h.act !== HeroAct.Dead && (owner === me || telegraphHit(tg, h.x, h.z))) {
        // 같은 틱에 터지는 낙인 피해는 모아 flushBrand가 한 번에 준다(첫 타 무적이 원 안 ×1.6을 지우지 않게 — #336 Codex P2와 같은 규칙)
        if (this.brandDmg === 0) { this.brandX = tg.x; this.brandZ = tg.z; }
        this.brandDmg += tn.damage * (owner === me ? tn.brand.bearerDmg : tn.brand.crowdDmg) * enr;
        if (owner !== me) this.brandHeavy = true;
      }
      const ev = g.emit(Ev.BossPattern); ev.x = tg.x; ev.z = tg.z; ev.a = WyvernPattern.brand; ev.b = BossKind.Wyvern; ev.c = -1; ev.d = owner;
      g.fireTelegraph(slot);
      this.syncBrand();
      let leftB = false;
      for (let k = 0; k < TEL_MAX; k++) if (this.telN[k] >= 0) { leftB = true; break; }
      if (!leftB) g.raid.state.pattern = -1;
      return;
    }
    if (tg.active && h.hp > 0 && h.act !== HeroAct.Dead && telegraphHit(tg, h.x, h.z)) {
      const b = g.boss, dmg = tn.damage * this.telMult[i] * enr;
      if (g.combat.hurtHero(dmg, b.x, b.z, this.telHeavy[i] === 1, 1)) this.view.hurtTaken++;
    }
    g.fireTelegraph(slot);
    // 남은 경고가 없으면 패턴 표시를 거둔다
    let left = false;
    for (let k = 0; k < TEL_MAX; k++) if (this.telN[k] >= 0) { left = true; break; }
    if (!left) g.raid.state.pattern = -1;
  }

  /** 칸 o의 지금 위치를 경고에 옮긴다(잿불 낙인). 모르면 그대로 둔다 */
  /** 이번 틱에 모은 낙인 피해를 한 번에(사건 처리 뒤·경고 진행 뒤) */
  private flushBrand(): void {
    if (this.brandDmg <= 0) return;
    const d = this.brandDmg, heavy = this.brandHeavy;
    this.brandDmg = 0; this.brandHeavy = false;
    const h = this.g.hero;
    if (h.hp > 0 && h.act !== HeroAct.Dead && this.g.combat.hurtHero(d, this.brandX, this.brandZ, heavy, 1)) this.view.hurtTaken++;
  }

  private ownerPos(o: number, tg: { x: number; z: number }): void {
    const g = this.g, c = this.client;
    if (!c) return;
    if (o === c.you) { tg.x = g.hero.x; tg.z = g.hero.z; return; }
    const mi = this.mercOfSlot[o];
    if (mi >= 0) { const m = g.raid.state.mercs[mi]; if (m) { tg.x = m.x; tg.z = m.z; } return; }
    const p = this.view.peers[o];
    if (p && p.active) { tg.x = p.x1; tg.z = p.z1; }
  }

  /** 낙인 표시(HUD·VFX·소리): 살아 있는 낙인 경고의 주인 칸 비트와 가장 이른 폭발까지 남은 틱. 넷 칸 번호를 로컬 파티 칸(0 = 내 영웅)으로 */
  private syncBrand(): void {
    const s = this.g.raid.state, c = this.client;
    let mask = 0, left = 0;
    const est = c ? c.serverTick() : 0;
    for (let k = 0; k < TEL_MAX; k++) {
      const o = this.telOwner[k];
      if (this.telN[k] < 0 || o < 0) continue;
      const local = c && o === c.you ? 0 : this.mercOfSlot[o] >= 0 ? this.mercOfSlot[o] + 1 : -1;
      if (local >= 0) mask |= 1 << local;
      const l = Math.max(0, this.telDue[k] - est);
      left = left === 0 ? l : Math.min(left, l);
    }
    s.brandSlots = mask; s.brandLeft = Math.round(left);
  }

  /** 모든 경고를 판정 없이 지운다(경직·쓰러짐으로 서버가 패턴을 취소했다, 다시 짓기 전, 판 끝) */
  private clearTeles(fire: boolean): void {
    const g = this.g;
    for (let i = 0; i < TEL_MAX; i++) {
      const slot = this.telSlot[i];
      if (slot >= 0) { if (fire) g.fireTelegraph(slot); else g.releaseTelegraph(slot); }
      this.telN[i] = -1; this.telSlot[i] = -1; this.telOwner[i] = -1;
    }
    g.raid.state.pattern = -1; g.raid.state.brandSlots = 0; g.raid.state.brandLeft = 0;
  }

  private stepTeles(est: number): void {
    const g = this.g;
    let live = 0, brand = false;
    for (let i = 0; i < TEL_MAX; i++) {
      if (this.telN[i] < 0) continue;
      live++;
      const slot = this.telSlot[i], tg = g.telegraphs[slot];
      if (!tg.active) { this.telN[i] = -1; this.telSlot[i] = -1; continue; }
      tg.tick = Math.max(0, Math.min(tg.total, est - this.telBorn[i]));
      // 잿불 낙인은 주인을 따라다닌다(내 영웅 = 로컬 위치, 다른 사람 = 보간 끝점, 용병 = 서버 스냅)
      const o = this.telOwner[i];
      if (o >= 0) { this.ownerPos(o, tg); brand = true; }
      if (est >= this.telDue[i]) { this.resolveTele(i); live--; }
    }
    this.view.teles = live;
    if (brand || g.raid.state.brandSlots !== 0) this.syncBrand();
  }

  // ── 불 장판 ────────────────────────────────────────
  private spawnFire(x: number, z: number, radius: number, ttl: number, clock: number, announce: boolean): void {
    const fires = this.g.raid.state.fires;
    let pick = null as (typeof fires)[number] | null;
    for (const f of fires) if (!f.active) { pick = f; break; }
    if (!pick) { pick = fires[0]; for (const f of fires) if (f.ttl < pick.ttl) pick = f; }
    pick.active = true; pick.x = x; pick.z = z; pick.radius = radius; pick.ttl = ttl; pick.total = Math.max(ttl, WYVERN.fire.ticks); pick.clock = Math.max(1, clock);
    if (announce) { const e = this.g.emit(Ev.RaidFire); e.x = x; e.z = z; e.a = radius; e.b = ttl; }
  }

  /** 불 장판 시계: 내 영웅만 0.5초마다 로컬 burn(raid.ts stepFires와 같은 피해). 용병·다른 사람은 서버가 센다 */
  private stepFires(): void {
    const g = this.g, h = g.hero, F = WYVERN.fire, dmg = Math.round(this.tn.damage * F.dmg * (g.raid.state.enraged ? WYVERN.enrage.dmgMult : 1));
    for (const f of g.raid.state.fires) {
      if (!f.active) continue;
      if (--f.ttl <= 0) { f.active = false; continue; }
      if (--f.clock > 0) continue;
      f.clock = F.every;
      if (h.hp > 0 && h.act !== HeroAct.Dead && Math.hypot(h.x - f.x, h.z - f.z) < f.radius) {
        g.combat.burnHero(dmg, 0);
        this.view.hurtTaken++;
      }
    }
  }

  // ── 보스·칸 ────────────────────────────────────────
  private syncBoss(c: RaidClient): void {
    const g = this.g, b = g.boss, t = c.boss, i = c.bossInfo, s = g.raid.state;
    if (!t.seen || b.kind !== BossKind.Wyvern) return;
    b.x = t.x; b.z = t.z; b.facing = t.f;
    if (i.state !== this.lastState) {
      // 상태가 바뀌었다: 서버 틱 기준(경고 born·사건 틱)이 가까우면 그만큼 지난 걸로, 아니면 이번 스냅 직전에 바뀐 걸로 본다
      const est = c.serverTick();
      const ref = this.changeRef >= 0 && est - this.changeRef <= 40 ? this.changeRef : i.k - 3;
      b.state = i.state as typeof b.state; b.stateTick = Math.max(0, Math.min(TIMER_MAX, Math.round(est - ref)));
      this.lastState = i.state;
    } else if (b.stateTick < TIMER_MAX) b.stateTick++;
    if (b.hitTick < TIMER_MAX) b.hitTick++;
    const dead = i.state === RBoss.Dying || i.state === RBoss.Dead;
    b.maxHp = i.maxHp;
    const server = dead ? 0 : Math.max(1, i.hp);
    if (server < this.lastServerHp) b.hitTick = 0; // 다른 사람이 쳤다: 같은 피격 번쩍임
    this.lastServerHp = server;
    // 표시 체력 = 서버 체력 − 최근 PRED_TICKS 안에 내가 보낸 타격 합(아직 서버 스냅에 안 실렸을 몫). 오래된 타격은 반영됐거나 버려졌으므로 쌓이지 않는다
    let pend = 0;
    if (!dead) for (let k = 0; k < 128; k++) if (this.tickNo - this.hitAt[k] < PRED_TICKS) pend += this.hitDmg[k];
    b.hp = dead ? 0 : Math.max(1, server - pend);
    b.phase2 = i.phase >= 2; s.phase = i.phase; s.enraged = i.enraged; s.enrageIn = i.enrageIn;
    s.air = t.y; b.air = t.y;
    b.stagger = i.stagger;
    b.invulnerable = i.state === RBoss.Intro || dead || t.y > 0;
    s.target = this.partyOf(i.target);
    if (i.taunt < 0) s.tauntTicks = 0;
  }

  /** 서버 칸 → 화면 파티 칸(0 영웅, 1..3 용병, -1 없음·다른 사람) */
  private partyOf(slot: number): number {
    const c = this.client;
    if (slot < 0 || !c) return -1;
    if (slot === c.you) return 0;
    const mi = this.mercOfSlot[slot];
    return mi !== undefined && mi >= 0 ? mi + 1 : -1;
  }

  /** 서버가 내 칸을 쓰러뜨려 둔 상태(재접속 직후 포함)면 쓰러짐을 유지하고 남은 시간을 보인다. 일어나는 것은 서버 Revive 사건·sync가 한다 */
  private reconcileMe(k: RaidTrack): void {
    const g = this.g, h = g.hero, c = this.client;
    if (!c) return;
    const snapK = c.bossInfo.k;
    if (k.down > 0) {
      // 내 Revive 사건보다 오래된 스냅의 쓰러짐은 이미 지난 일이다
      if (snapK <= this.reviveK) return;
      if (h.act !== HeroAct.Dead) this.forceDown();
      this.view.reviveTicks = k.down; this.reviveGrace = REVIVE_GRACE_TICKS;
    } else if (k.hp > 0 && h.act === HeroAct.Dead && snapK > this.downK + 12) {
      // 내가 쓰러진 뒤의 건강한 스냅: 서버는 이미 일으켰다(Revive 사건을 놓쳤다)
      this.reviveHero();
    }
  }

  private syncSlot(k: RaidTrack): void {
    const c = this.client;
    if (!c) return;
    const g = this.g, slot = k.id;
    if (k.kind >= 10) {
      const mi = k.kind - 10;
      if (mi < 0 || mi >= MERC_COUNT) return;
      if (slot >= 0 && slot < PEER_SLOTS) this.mercOfSlot[slot] = mi;
      const m = g.raid.state.mercs[mi];
      this.mercSeen |= 1 << mi;
      m.x = k.x; m.z = k.z; m.facing = k.f;
      m.maxHp = Math.max(1, k.max); m.hp = k.hp; m.down = k.down;
      if (k.act !== this.mercAct[mi]) { this.mercAct[mi] = k.act; m.actTick = 0; } else if (m.actTick < TIMER_MAX) m.actTick++;
      m.act = k.act;
      if (m.hurtTick < TIMER_MAX) m.hurtTick++;
      m.evading = this.evadeLeft[mi] > 0;
      return;
    }
    if (slot === c.you) { this.reconcileMe(k); return; }
    if (slot < 0 || slot >= PEER_SLOTS || !k.seen) return;
    // 다른 사람: 보간 구간 → FieldPeer(PeersVisual 입력). 서버 동작(RaidAct)을 영웅 동작(HeroAct)으로
    const p = this.view.peers[slot];
    this.mercOfSlot[slot] = -1;
    p.active = true; p.visible = true; p.id = slot;
    p.t0 = k.at; p.x0 = k.ax; p.z0 = k.az; p.f0 = k.af;
    p.t1 = k.bt; p.x1 = k.bx; p.z1 = k.bz; p.f1 = k.bf;
    p.act = k.down > 0 || k.act === RaidAct.Down ? HeroAct.Dead : k.act === RaidAct.Attack || k.act === RaidAct.Cast ? HeroAct.Attack : HeroAct.Idle;
    const room = c.room;
    if (!room) return;
    for (let j = 0; j < room.members.length; j++) {
      const mem = room.members[j];
      if (mem.slot !== slot) continue;
      if (p.name !== mem.name) { p.name = mem.name; p.serial++; }
      p.hero = mem.hero === 'lyra' ? 1 : 0;
      break;
    }
  }

  // ── 끝 ────────────────────────────────────────────
  private finish(e: Extract<RaidNetEvent, { t: 'end' }>): void {
    const v = this.view, r = v.resultBox, rw = e.reward;
    r.seq++; r.ok = e.ok; r.reason = e.reason; r.ms = e.ms; r.eligible = e.eligible;
    r.n = rw ? rw.n : 0; r.gold = rw ? rw.gold : 0; r.set = rw ? rw.set : ''; r.legend = rw ? rw.legend : ''; r.items = rw ? rw.items : 0; r.weekLeft = e.weekLeft;
    r.leaveTicks = v.active ? RAID_END_LEAVE_TICKS : 0;
    v.result = r;
    if (!v.active) return;
    this.ended = true;
    const g = this.g, f = g.flow, res = g.result, h = g.hero;
    this.clearTeles(false);
    for (const fp of g.raid.state.fires) fp.active = false;
    g.releaseAllTelegraphs();
    res.cleared = e.ok; res.clearTicks = Math.round((e.ms / 1000) * TICK_HZ); res.maxCombo = g.combo.best;
    res.starMask = e.ok ? StarBit.Clear : 0; res.stars = e.ok ? 1 : 0;
    res.cards.fill(0); res.pickedCard = 0;
    const hardRun = g.raid.state.diff === 'hard';
    res.resultId = 'net:' + this.runId; res.stageId = hardRun ? 'raid_wyvern.hard' : 'raid_wyvern'; res.raidDiff = hardRun ? 'hard' : 'normal';
    res.raidFirst = false; res.raidFirstGold = 0; res.raidFirstLegend = ''; res.raidFirstStones = 0;
    // 하드 첫 클리어(C13): 보상은 서버 우편(주체마다 한 번). 칭호만 이 기기에 표시 영수증으로 남긴다 —
    // 정산 표시(raidStore)를 먼저 쓰고, 그게 성공했을 때만 골드 0 영수증을 기록한다(솔로 정산이 확정 전설을 다시 넣지 않게)
    if (e.ok && e.first) {
      res.raidFirst = true; res.raidFirstGold = e.first.gold; res.raidFirstLegend = e.first.legend;
      const fid = raidFirstId('raid_wyvern', 'hard');
      if (!g.save.hasResult(fid) && g.raidStore.claim(fid)) {
        g.save.recordResult({ id: fid, region: 'r2', baseGold: 0, clearTicks: res.clearTicks, maxCombo: res.maxCombo, stars: res.stars, starMask: res.starMask,
          kills: 0, damageDealt: res.damageDealt, cards: [0, 0, 0], pickedCard: 0, bonus: true, raid: 'raid_wyvern', raidHero: g.heroId, raidN: 0, raidProtect: 0 });
      }
    }
    res.raid = 'raid_wyvern'; res.raidLimit = 3; res.raidN = r.n; res.gold = r.gold; res.raidSet = r.set; res.raidLegend = r.legend;
    res.raidStones = rw ? rw.stones : 0; res.raidProtect = rw ? rw.protect : 0;
    res.savePending = false; res.saveRecovered = false;
    res.raidNote = raidNetNote(e.ok, e.reason, r.n, e.weekLeft);
    f.paused = false; f.sceneTick = 0;
    if (e.ok) {
      // 경험치는 로컬(지역2 보스 EXP × 3, 판 id로 한 번만) — 장비·골드 보상은 서버 우편함으로 온다
      g.grantXp(XP_BOSS.r2 * RAID_XP_MULT, XpCause.Boss, h.x, h.z);
      g.growth.commitRun(res.resultId);
      f.scene = Scene.Result;
      f.objectiveLabel = res.raidNote;
      if (h.act !== HeroAct.Dead) { h.act = HeroAct.Victory; h.actTick = 0; }
      g.emit(Ev.StageClear);
      const ev = g.emit(Ev.StageResult); ev.a = 2; ev.b = 0; ev.c = r.n; ev.d = 0;
      g.emit(Ev.SceneChange).a = Scene.Result;
    } else {
      f.scene = Scene.Defeat;
      f.objectiveLabel = res.raidNote;
      h.act = HeroAct.Dead; h.actTick = 0; h.invulnerable = false; h.y = 0;
      g.growth.commitRun();
      g.emit(Ev.Defeat);
      g.emit(Ev.SceneChange).a = Scene.Defeat;
    }
    g.skillFx.clear();
    g.arrows.clear();
  }

  /** 「나가기」 단추(raidNetLeave 명령): 판 중이면 연결을 놓고 마을로 */
  leaveByRequest(): void { if (this.view.active) this.leave(); }

  /** 판을 접고 마을로(연결도 나간다) */
  private leave(): void {
    const g = this.g, v = this.view;
    const was = v.active;
    this.detach();
    if (was) g.flowController.loadMap('village');
  }

  /** 판 연결을 놓는다(화면 전환은 호출한 쪽이 이미 했거나 이어서 한다) */
  private detach(): void {
    const g = this.g, v = this.view;
    v.active = false; v.waiting = false; v.barrier = false; v.reviveTicks = 0;
    this.ended = false; this.entered = false;
    g.raid.netMode = false;
    this.clearTeles(false);
    if (v.result) v.result.leaveTicks = 0;
    for (let i = 0; i < PEER_SLOTS; i++) { v.peers[i].active = false; v.peers[i].visible = false; }
    for (const m of g.raid.state.mercs) m.visible = true;
    this.client?.leave();
  }
}

/** 결과 화면 안내 한 줄: 보상은 우편함으로 간다(서버 사유별 문구) */
export function raidNetNote(ok: boolean, reason: string, n: number, weekLeft: number): string {
  if (reason === 'expired') return '자리 유지 시간이 지나 판에서 빠졌어요 — 보상은 우편함을 확인하세요.';
  if (reason === 'lost') return '연결이 끊긴 사이 판이 끝났어요 — 보상은 우편함을 확인하세요.';
  if (!ok) {
    const why = reason === 'wipe' ? '전원이 쓰러졌다' : reason === 'time' ? '시간이 다 됐다' : reason === 'abandon' ? '파티가 모두 떠났다' : '토벌에 실패했다';
    return '토벌 실패 · ' + why + ' · 마을로 돌아간다.';
  }
  if (n > 0) return '토벌 성공! 보상은 우편함으로 보냈다' + (weekLeft >= 0 ? ' · 이번 주 남은 보상 ' + weekLeft + '회' : '') + '.';
  const why = reason === 'link-required' ? 'Google 계정을 연결하면 보상을 받아요' : reason === 'low-contribution' ? '딜 기여가 부족해 보상이 없어요' : reason === 'weekly-limit' ? '이번 주 보상을 모두 받았어요' : reason === 'ledger' ? '보상 기록이 늦어요 · 우편함을 확인하세요' : '보상이 없어요';
  return '토벌 성공! ' + why + '.';
}
